import Select from "react-select";

const isMobile = window.innerWidth <= 768;

// react-select styles tuned to match the smartApply form fields
// (.form-control / .form-label tokens from smartApply's design system).
const getMultiStyles = (hasError) => ({
    control: (provided, state) => ({
        ...provided,
        width: "100%",
         minHeight: isMobile ? "44px" : "38px",
        backgroundColor: "var(--surface-bg, #ffffff)",
        borderRadius: "8px",
        padding: "1px 4px",
        fontSize: "var(--font-size-base, 0.6875rem)",
        fontWeight: 400,
        fontFamily: "var(--font-family-base, 'Inter', sans-serif)",
        color: "var(--brand-text, #1F222A)",
        border: hasError
            ? "1px solid var(--color-rose-400, #FB7185)"
            : "1px solid var(--ui-border, #d0d5dd)",
        boxShadow: state.isFocused
            ? hasError
                ? "0 0 0 4px rgba(251, 113, 133, 0.15)"
                : "0 0 0 4px rgba(59, 124, 242, 0.15)"
            : "none",
        "&:hover": {
            borderColor: hasError ? "var(--color-rose-400, #FB7185)" : "var(--ui-border, #d0d5dd)",
        },
        transition: "all 0.3s ease",
    }),

    valueContainer: (provided) => ({
        ...provided,
        padding: "2px 6px",
        gap: "4px",
    }),

    placeholder: (provided) => ({
        ...provided,
        color: "var(--color-gray-400, #9ca3af)",
        fontSize: "var(--font-size-base, 0.6875rem)",
        fontWeight: 400,
    }),

    input: (provided) => ({
        ...provided,
        color: "var(--brand-text, #1F222A)",
        margin: 0,
        padding: 0,
    }),

    multiValue: (provided) => ({
        ...provided,
        backgroundColor: "var(--color-info-bg, #eef2ff)",
        border: "1px solid var(--brand-primary, #081DA5)",
        borderRadius: "16px",
        overflow: "hidden",
    }),

    multiValueLabel: (provided) => ({
        ...provided,
        color: "var(--brand-primary, #081DA5)",
        fontSize: "var(--font-size-sm, 0.61rem)",
        fontWeight: 500,
        padding: "2px 4px 2px 8px",
    }),

    multiValueRemove: (provided) => ({
        ...provided,
        color: "var(--brand-primary, #081DA5)",
        borderRadius: "0 16px 16px 0",
        "&:hover": {
            backgroundColor: "var(--brand-primary, #081DA5)",
            color: "#fff",
        },
    }),

    menu: (provided) => ({
        ...provided,
        borderRadius: "8px",
        border: "1px solid var(--ui-border, #d0d5dd)",
        boxShadow: "0 4px 12px -2px rgba(0,0,0,0.1)",
        zIndex: 1050,
        overflow: "hidden",
    }),

    menuList: (provided) => ({
        ...provided,
        padding: "4px 0",
        maxHeight:'200px'
    }),

    option: (provided, state) => ({
        ...provided,
        fontSize: "var(--font-size-base, 0.6875rem)",
        color: state.isSelected ? "var(--brand-primary, #081DA5)" : "var(--brand-text, #1F222A)",
        backgroundColor: state.isSelected
            ? "var(--color-info-bg, #eef2ff)"
            : state.isFocused
                ? "var(--ui-gray-bg, #e5e7eb)"
                : "transparent",
        cursor: "pointer",
        "&:active": {
            backgroundColor: "var(--color-info-bg, #eef2ff)",
        },
    }),

    indicatorSeparator: () => ({ display: "none" }),

    dropdownIndicator: (provided, state) => ({
        ...provided,
        color: "var(--ui-text-muted, #667085)",
        padding: "4px",
        transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.2s",
        "&:hover": { color: "var(--brand-primary, #081DA5)" },
    }),

    clearIndicator: (provided) => ({
        ...provided,
        color: "var(--ui-text-muted, #667085)",
        padding: "4px",
        "&:hover": { color: "var(--color-danger-text, #dc3545)" },
    }),
});

/**
 * MultiSelect – react-select based multi/single select styled to match
 * the smartApply form fields. API mirrors CustomSelect.
 *
 * Props:
 *  - options: array of objects (use labelKey/valKey, or { value, label })
 *  - value: array of selected values (or single value when isMulti=false)
 *  - handleChange / onChange: receives next value(s)
 *  - name: forwarded via onChange({ target: { name, value } }) if provided
 *  - labelKey / valKey, placeholder, label, errorMsg, isDisabled,
 *    isSearchable, isClearable, isMulti (defaults true)
 */
function MultiSelect(props) {
    let {
        options = [],
        placeholder = "Select...",
        handleChange,
        onChange,
        name,
        isMulti = false,
        value = [],
        labelKey,
        valKey,
        isSearchable = true,
        isClearable = false,
        label = "",
        errorMsg = "",
        isDisabled = false,
    } = props;

    const normalized = options?.map((o) =>
        labelKey || valKey
            ? { label: o[labelKey], value: o[valKey] }
            : { label: o.label, value: o.value }
    ) || [];

    const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

    const emit = (next) => {
        if (typeof onChange === "function") {
            onChange({ target: { name, value: next } });
        }
        if (typeof handleChange === "function") {
            handleChange(next);
        }
    };

    return (
        <div style={{ width: "100%" }}>
            {label && <label className="form-label mb-1">{label}</label>}
            <Select
                placeholder={placeholder}
                options={normalized}
                isMulti={isMulti}
                isSearchable={isSearchable}
                isClearable={isClearable}
                isDisabled={isDisabled}
                styles={getMultiStyles(errorMsg)}
                classNamePrefix="select"
                value={
                    isMulti
                        ? normalized.filter((o) => selectedValues.includes(o.value))
                        : normalized.find((o) => o.value === value) || null
                }
                onChange={(selected) => {
                    if (isMulti) {
                        emit((selected || []).map((item) => item.value));
                    } else {
                        emit(selected?.value ?? "");
                    }
                }}
            />
            {errorMsg && typeof errorMsg === "string" && (
                <div className="text-danger small mt-1">{errorMsg}</div>
            )}
        </div>
    );
}

export default MultiSelect;
